/**
 * Brimi AI — new-user product tour.
 *
 * Uses driver.js. The tour highlights the four core flows in order:
 *   Subjects → open a Chapter → Notes → Chat → Quiz → Past papers.
 *
 * The tour is auto-triggered ONCE for new signups (kept in localStorage), and
 * can be replayed anytime from the header "?" button.
 */

import { driver } from "driver.js";
import "driver.js/dist/driver.css";

const LS_KEY = "brimi_tour_done_v1";

export function hasSeenTour() {
  try {
    return localStorage.getItem(LS_KEY) === "1";
  } catch {
    return true; // fail safe — assume seen so we don't nag
  }
}

export function markTourSeen() {
  try {
    localStorage.setItem(LS_KEY, "1");
  } catch {
    /* ignore */
  }
}

/**
 * Kick off the tour. `navigate` is the react-router navigate() function so
 * we can walk the student through actual routes.
 * `onFinish` is invoked when the tour ends OR is skipped.
 */
export function startTour({ navigate, onFinish }) {
  const d = driver({
    showProgress: true,
    allowClose: true,
    animate: true,
    smoothScroll: true,
    overlayOpacity: 0.55,
    stagePadding: 6,
    stageRadius: 12,
    nextBtnText: "Next →",
    prevBtnText: "← Back",
    doneBtnText: "Let's go!",
    progressText: "Step {{current}} of {{total}}",
    onDestroyed: () => {
      markTourSeen();
      onFinish?.();
    },
  });

  d.setSteps([
    {
      element: '[data-testid="brand-link"]',
      popover: {
        title: "Welcome to Brimi AI 👋",
        description:
          "Your AI study buddy for ICSE & ISC Class IX–XII. Quick 30-second tour so you get the most out of it. Tap the ✕ anytime to skip.",
        side: "bottom",
        align: "start",
      },
    },
    {
      // Sits on the header at every route — but on Subjects/Home we scroll to the grid too.
      element: '[data-testid="header-progress-btn"]',
      popover: {
        title: "Track your progress",
        description:
          "Every quiz you attempt, every paper you grade — Brimi remembers. Click Progress anytime to see your streaks, scores and weak chapters.",
        side: "bottom",
        align: "end",
      },
    },
    {
      element: 'body',
      popover: {
        title: "Step 1 — Pick a Subject",
        description:
          "From the home screen, choose Computer, English or Mathematics for your class. Each subject has 10–20 chapters aligned to the ICSE / ISC syllabus.",
        side: "top",
        align: "center",
      },
      onHighlightStarted: () => {
        if (!location.pathname.startsWith("/subjects")) navigate("/subjects");
      },
    },
    {
      element: 'body',
      popover: {
        title: "Step 2 — Open a Chapter → Read the Notes",
        description:
          "Every chapter has a detailed Notes tab — line-by-line explanations, difficult word meanings, and 2 extract-based questions with model answers. Read these first before you chat.",
        side: "top",
        align: "center",
      },
    },
    {
      element: 'body',
      popover: {
        title: "Step 3 — Ask doubts in Chat",
        description:
          "Confused about anything? Open the Chat tab of that chapter and ask Brimi. It quotes your textbook, shows step-by-step math, and even runs Java/Python code for you.",
        side: "top",
        align: "center",
      },
    },
    {
      element: 'body',
      popover: {
        title: "Step 4 — Test yourself with a Quiz",
        description:
          "Once you feel confident, take the 10-question quiz. Every retake reshuffles the questions so it's never the same paper twice.",
        side: "top",
        align: "center",
      },
    },
    {
      element: 'body',
      popover: {
        title: "Step 5 — Solve Past Year Papers",
        description:
          "For real board-exam practice, tap Past Papers on any chapter. Brimi grades your attempt sentence-by-sentence with feedback — like a private tutor.",
        side: "top",
        align: "center",
      },
    },
    {
      element: '[data-testid="header-upgrade-btn"], [data-testid="header-tour-btn"]',
      popover: {
        title: "20-day free trial 🎁",
        description:
          "Everything above is free for 20 days. No card needed. If you love it, upgrade for just ₹299/month. You can replay this tour anytime from the ? button in the header.",
        side: "bottom",
        align: "end",
      },
    },
  ]);

  d.drive();
}
